"use client"
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { KpiCard, KpiCardSkeleton } from "@/components/ui/analytics/kpi-card"
import { formatCurrency } from "@/components/shopify/attribution-dashboard/utils"
import { ProductMultiSelect } from "./product-multi-select"
import { FamilyMultiSelect } from "./family-multi-select"
import { ShoppingCart, Package, TrendingUp, Users, Search, Info } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { OrdersLink } from "./orders-link"
import type {
  CrossSellGroupBy,
  FamilyFocusSummary,
  FocusProductSummary,
} from "./types"
import type { ProductOption } from "@/types/shopify"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




interface FocusProductViewProps {
  integrationId: string
  monthStartUtc: string
  monthEndUtc: string
  minimumOrderTotal: number
  /** Default "product". */
  groupBy?: CrossSellGroupBy
  selectedProducts: ProductOption[]
  onProductsChange: (products: ProductOption[]) => void
  focusFamilyKeys?: string[]
  onFocusFamilyKeysChange?: (keys: string[]) => void
  focus: FocusProductSummary | null
  focusByFamily?: FamilyFocusSummary | null
  isLoading: boolean
  error?: string | null
}

export function FocusProductView({
  integrationId,
  monthStartUtc,
  monthEndUtc,
  minimumOrderTotal,
  groupBy = "product",
  selectedProducts,
  onProductsChange,
  focusFamilyKeys = [],
  onFocusFamilyKeysChange,
  focus,
  focusByFamily = null,
  isLoading,
  error,
}: FocusProductViewProps) {
  const {locale: uiFormattingLocale} = useUiControlLabels()


  const tUi = useTranslations("Shopify.ShopifyCrossSellFocusProductView")

  const isFamilyMode = groupBy === "family"
  const data = isFamilyMode ? focusByFamily : focus
  const hasSelection = isFamilyMode ? focusFamilyKeys.length > 0 : selectedProducts.length > 0
  const productIdsForOrdersLink = isFamilyMode ? undefined : focus?.product_ids
  return (
    <div className="space-y-4">
      {/* Selector — products in product mode, families in family mode.
          Each picker is single-purpose:
          - Product mode disables the families section in ProductMultiSelect
            (`enableFamilies={false}`) so the dropdown shows only products.
          - Family mode renders FamilyMultiSelect — searchable list of
            Product Families with a count badge, populated from the same
            `useProductFamilies` hook everything else uses (DRY). */}
      <div className="max-w-lg">
        {isFamilyMode ? (
          <>
            <label className="text-sm font-medium mb-1 block">
              <span key={tUi("selectProductFamilyOrFamiliesToAnalyze")}>{tUi("selectProductFamilyOrFamiliesToAnalyze")}</span></label>
            <p className="text-xs text-muted-foreground mb-2">
              <span key={tUi("pickOneOrMoreFamiliesMultipleSelectionsAre")}>{tUi("pickOneOrMoreFamiliesMultipleSelectionsAre")}</span></p>
            <FamilyMultiSelect
              integrationId={integrationId}
              selected={focusFamilyKeys}
              onSelectionChange={(keys) => onFocusFamilyKeysChange?.(keys)}
              maxSelect={10}
              placeholder={tUi("searchFamilies")}
            />
          </>
        ) : (
          <>
            <label className="text-sm font-medium mb-1 block">
              <span key={tUi("selectProductSToAnalyze")}>{tUi("selectProductSToAnalyze")}</span></label>
            <p className="text-xs text-muted-foreground mb-2">
              <span key={tUi("selectMultipleProductsToAnalyzeAsASet")}>{tUi("selectMultipleProductsToAnalyzeAsASet")}</span></p>
            <ProductMultiSelect
              integrationId={integrationId}
              monthStartUtc={monthStartUtc}
              monthEndUtc={monthEndUtc}
              minimumOrderTotal={minimumOrderTotal}
              selected={selectedProducts}
              maxSelect={100}
              enableFamilies={false}
              onSelectionChange={onProductsChange}
              placeholder={tUi("searchForAProduct")}
            />
          </>
        )}
      </div>

      {/* Loading */}
      {isLoading && hasSelection && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCardSkeleton />
          <KpiCardSkeleton />
          <KpiCardSkeleton />
          <KpiCardSkeleton />
        </div>
      )}

      {/* No product selected */}
      {!hasSelection && !isLoading && (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Search className="h-8 w-8 mb-2" />
          <p key={isFamilyMode
              ? tUi("enterAFamilyKeyAboveToSeeIts")
              : tUi("selectAProductAboveToSeeItsCo")} className="text-sm">
            {isFamilyMode
              ? tUi("enterAFamilyKeyAboveToSeeIts")
              : tUi("selectAProductAboveToSeeItsCo")}
          </p>
        </div>
      )}

      {/* Focus results — KPIs share shape between FocusProductSummary and
          FamilyFocusSummary; only OrdersLink productIds differs (family mode
          can't filter by product_id). */}
      {data && !isLoading && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label={tUi("totalOrders")}
              icon={ShoppingCart}
              formattedValue={
                <OrdersLink
                  integrationId={integrationId}
                  monthStartUtc={monthStartUtc}
                  monthEndUtc={monthEndUtc}
                  productIds={productIdsForOrdersLink}
                >
                  {data.total_orders.toLocaleString(uiFormattingLocale)}
                </OrdersLink>
              }
              subtitle={
                <span>
                  {data.solo_order_ids?.length ? (
                    <OrdersLink
                      integrationId={integrationId}
                      monthStartUtc={monthStartUtc}
                      monthEndUtc={monthEndUtc}
                      orderIds={data.solo_order_ids}
                      productIds={productIdsForOrdersLink}
                      className="text-muted-foreground"
                    ><TranslationText>{tUi.rich("value0Solo", { value0: String(data.solo_orders) })}</TranslationText></OrdersLink>
                  ) : (
                    <><TranslationText>{tUi.rich("value0Solo", { value0: String(data.solo_orders) })}</TranslationText></>
                  )}
                  {", "}
                  {data.bundle_order_ids?.length ? (
                    <OrdersLink
                      integrationId={integrationId}
                      monthStartUtc={monthStartUtc}
                      monthEndUtc={monthEndUtc}
                      orderIds={data.bundle_order_ids}
                      productIds={productIdsForOrdersLink}
                      className="text-muted-foreground"
                    ><TranslationText>{tUi.rich("value0MultiItem", { value0: String(data.bundle_orders) })}</TranslationText></OrdersLink>
                  ) : (
                    <><TranslationText>{tUi.rich("value0MultiItem", { value0: String(data.bundle_orders) })}</TranslationText></>
                  )}
                </span>
              }
            />
            <KpiCard
              label={tUi("soloAvgOrderValue")}
              icon={Package}
              formattedValue={formatCurrency(data.solo_aov)}
              subtitle={
                data.solo_order_ids?.length ? (
                  <OrdersLink
                    integrationId={integrationId}
                    monthStartUtc={monthStartUtc}
                    monthEndUtc={monthEndUtc}
                    orderIds={data.solo_order_ids}
                    productIds={productIdsForOrdersLink}
                    className="text-muted-foreground"
                  >
                    {data.solo_orders} <span><span key={tUi("soloOrders")}>{tUi("soloOrders")}</span></span>
                  </OrdersLink>
                ) : (
                  `${data.solo_orders} solo orders`
                )
              }
            />
            <KpiCard
              label={tUi("multiItemAvgOrderValue")}
              icon={TrendingUp}
              formattedValue={formatCurrency(data.bundle_aov)}
              subtitle={
                data.bundle_order_ids?.length ? (
                  <OrdersLink
                    integrationId={integrationId}
                    monthStartUtc={monthStartUtc}
                    monthEndUtc={monthEndUtc}
                    orderIds={data.bundle_order_ids}
                    productIds={productIdsForOrdersLink}
                    className="text-muted-foreground"
                  >
                    {data.bundle_orders} <span><span key={tUi("multiItemOrders")}>{tUi("multiItemOrders")}</span></span>
                  </OrdersLink>
                ) : (
                  `${data.bundle_orders} multi-item orders`
                )
              }
              tooltip={
                isFamilyMode
                  ? tUi("averageTotalOrderValueAllItemsWhenThe")
                  : tUi("averageTotalOrderValueAllItemsWhenThe2")
              }
            />
            <KpiCard
              label={tUi("multiItemUplift")}
              icon={Users}
              formattedValue={
                data.solo_aov > 0
                  ? `+${Math.round(((data.bundle_aov - data.solo_aov) / data.solo_aov) * 100)}%`
                  : "N/A"
              }
              subtitle={tUi("avgOrderValueIncreaseInMultiItemOrders")}
              tooltip={tUi("percentageDifferenceBetweenMultiItemAOVAndSolo")}
            />
          </div>

          {/* Revenue breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <KpiCard
              label={tUi("soloRevenue")}
              icon={Package}
              formattedValue={formatCurrency(data.solo_revenue)}
              subtitle={tUi("value0OfOrders", { value0: String(data.total_orders > 0 ? Math.round((data.solo_orders / data.total_orders) * 100) : 0) })}
            />
            <KpiCard
              label={tUi("multiItemRevenue")}
              icon={TrendingUp}
              formattedValue={formatCurrency(data.bundle_revenue)}
              subtitle={tUi("value0OfOrders", { value0: String(data.total_orders > 0 ? Math.round((data.bundle_orders / data.total_orders) * 100) : 0) })}
              tooltip={
                isFamilyMode
                  ? tUi("totalOrderRevenueAllItemsFromOrdersWhere")
                  : tUi("totalOrderRevenueAllItemsFromOrdersWhere2")
              }
            />
          </div>

          {/* Companion table — different columns per mode (product vs family). */}
          {isFamilyMode ? (
            (focusByFamily?.companions.length ?? 0) > 0 ? (
              <FamilyCompanionsTable
                companions={focusByFamily!.companions}
                integrationId={integrationId}
                monthStartUtc={monthStartUtc}
                monthEndUtc={monthEndUtc}
              />
            ) : (
              <div className="text-sm text-muted-foreground text-center py-4">
                <span key={tUi("thisFamilyIsAlwaysPurchasedSoloNoCompanion")}>{tUi("thisFamilyIsAlwaysPurchasedSoloNoCompanion")}</span></div>
            )
          ) : null}

          {/* Companion products table */}
          {!isFamilyMode && focus && focus.companions.length > 0 && (
            <div>
              <h3 className="text-sm font-medium mb-2">
                <span><span key={tUi("topCompanions")}>{tUi("topCompanions")}</span></span>{focus.companions.length}<span>)</span>
              </h3>
              <TooltipProvider delayDuration={300}>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">#</TableHead>
                      <TableHead><span key={tUi("companionProduct")}>{tUi("companionProduct")}</span></TableHead>
                      <TableHead><span key={tUi("timesTogether")}>{tUi("timesTogether")}</span></TableHead>
                      <TableHead>
                        <span><span key={tUi("ofOrders")}>{tUi("ofOrders")}</span></span><sup>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="inline h-2.5 w-2.5 cursor-help text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[250px]">
                              <p className="text-xs"><span key={tUi("percentageOfAllFocusProductOrdersThatInclude")}>{tUi("percentageOfAllFocusProductOrdersThatInclude")}</span></p>
                            </TooltipContent>
                          </Tooltip>
                        </sup>
                      </TableHead>
                      <TableHead>
                        <span><span key={tUi("combinedRevenue")}>{tUi("combinedRevenue")}</span></span><sup>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="inline h-2.5 w-2.5 cursor-help text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[250px]">
                              <p className="text-xs"><span key={tUi("totalRevenueFromBothTheFocusProductAnd")}>{tUi("totalRevenueFromBothTheFocusProductAnd")}</span></p>
                            </TooltipContent>
                          </Tooltip>
                        </sup>
                      </TableHead>
                      <TableHead>
                        <span><span key={tUi("companionRevenue")}>{tUi("companionRevenue")}</span></span><sup>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="inline h-2.5 w-2.5 cursor-help text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[250px]">
                              <p className="text-xs"><span key={tUi("revenueFromThisCompanionProductAloneInThe")}>{tUi("revenueFromThisCompanionProductAloneInThe")}</span></p>
                            </TooltipContent>
                          </Tooltip>
                        </sup>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {focus.companions.map((comp, idx) => (
                      <TableRow key={comp.product_id}>
                        <TableCell className="text-muted-foreground text-xs">
                          {idx + 1}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium text-sm">{comp.title}</span>
                          {comp.sku && (
                            <span className="block text-[11px] text-muted-foreground/70">{comp.sku}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {comp.order_ids?.length ? (
                            <OrdersLink
                              integrationId={integrationId}
                              monthStartUtc={monthStartUtc}
                              monthEndUtc={monthEndUtc}
                              orderIds={comp.order_ids}
                            >
                              {comp.times_bought_together}
                            </OrdersLink>
                          ) : (
                            comp.times_bought_together
                          )}
                        </TableCell>
                        <TableCell>{comp.pct_of_focus_orders}<span>%</span></TableCell>
                        <TableCell>{formatCurrency(comp.revenue_together)}</TableCell>
                        <TableCell>{formatCurrency(comp.companion_revenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              </TooltipProvider>
            </div>
          )}

          {!isFamilyMode && focus && focus.companions.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-4">
              <span key={tUi("thisProductIsAlwaysPurchasedSoloNoCompanion")}>{tUi("thisProductIsAlwaysPurchasedSoloNoCompanion")}</span></div>
          )}
        </>
      )}

      {/* Selection made but no data (only show when not an error) */}
      {hasSelection && !data && !isLoading && !error && (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Search className="h-8 w-8 mb-2" />
          <p key={isFamilyMode
              ? tUi("noOrdersFoundForThisFamilyInThe")
              : tUi("noOrdersFoundForThisProductInThe")} className="text-sm">
            {isFamilyMode
              ? tUi("noOrdersFoundForThisFamilyInThe")
              : tUi("noOrdersFoundForThisProductInThe")}
          </p>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Family-mode companions table
// ---------------------------------------------------------------------------

function FamilyCompanionsTable({
  companions,
  integrationId,
  monthStartUtc,
  monthEndUtc,
}: {
  companions: FamilyFocusSummary["companions"]
  integrationId: string
  monthStartUtc: string
  monthEndUtc: string
}) {
  const tUi = useTranslations("Shopify.ShopifyCrossSellFocusProductView")

  return (
    <div>
      <h3 className="text-sm font-medium mb-2">
        <span><span key={tUi("topCompanionFamilies")}>{tUi("topCompanionFamilies")}</span></span>{companions.length}<span>)</span>
      </h3>
      <TooltipProvider delayDuration={300}>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">#</TableHead>
                <TableHead><span key={tUi("companionFamily")}>{tUi("companionFamily")}</span></TableHead>
                <TableHead><span key={tUi("timesTogether")}>{tUi("timesTogether")}</span></TableHead>
                <TableHead>
                  <span><span key={tUi("ofOrders")}>{tUi("ofOrders")}</span></span>
                  <sup>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="inline h-2.5 w-2.5 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[250px]">
                        <p className="text-xs">
                          <span key={tUi("percentageOfAllFocusFamilyOrdersThatInclude")}>{tUi("percentageOfAllFocusFamilyOrdersThatInclude")}</span></p>
                      </TooltipContent>
                    </Tooltip>
                  </sup>
                </TableHead>
                <TableHead><span key={tUi("combinedRevenue")}>{tUi("combinedRevenue")}</span></TableHead>
                <TableHead><span key={tUi("companionRevenue")}>{tUi("companionRevenue")}</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companions.map((comp, idx) => (
                <TableRow key={comp.family_key}>
                  <TableCell className="text-muted-foreground text-xs">
                    {idx + 1}
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-sm">{comp.label}</span>
                    <span className="block text-[11px] text-muted-foreground/70">
                      {comp.family_key}
                    </span>
                  </TableCell>
                  <TableCell>
                    {comp.order_ids?.length ? (
                      <OrdersLink
                        integrationId={integrationId}
                        monthStartUtc={monthStartUtc}
                        monthEndUtc={monthEndUtc}
                        orderIds={comp.order_ids}
                      >
                        {comp.times_bought_together}
                      </OrdersLink>
                    ) : (
                      comp.times_bought_together
                    )}
                  </TableCell>
                  <TableCell>{comp.pct_of_focus_orders}<span>%</span></TableCell>
                  <TableCell>{formatCurrency(comp.revenue_together)}</TableCell>
                  <TableCell>{formatCurrency(comp.companion_revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </TooltipProvider>
    </div>
  )
}
